import React, { useState, useEffect } from "react";
import { Navigation } from "../components/Navigation";
import { COPY } from "../constants/copy";

interface TimeMeaningProps {
  onNext: () => void;
  onPrevious: () => void;
}

// Counter component with animation
const CounterCard: React.FC<{
  label: string;
  value: number;
  unit: string;
  delay: number;
  description: string;
}> = ({ label, value, unit, delay, description }) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => {
      const duration = 2000; // 2 seconds
      const startTime = Date.now();

      const animate = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);
        setCount(Math.floor(progress * value));

        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          setCount(value);
        }
      };

      requestAnimationFrame(animate);
    }, delay);

    return () => clearTimeout(timer);
  }, [value, delay]);

  return (
    <div className="animate-slide-up">
      <div className="bg-gradient-to-br from-blushPink to-softRose dark:from-darkCard dark:to-gray-700 rounded-lg p-4 md:p-6 border border-softRose dark:border-gray-600 shadow-md hover:shadow-lg transition-shadow duration-300 h-full">
        <div className="space-y-3">
          {/* Number display */}
          <div className="space-y-1">
            <p className="text-3xl md:text-4xl font-light text-heartRed dark:text-softRose tabular-nums">
              {count.toLocaleString()}
            </p>
            <p className="text-xs text-mutedGray dark:text-gray-400 font-light uppercase tracking-widest">
              {unit}
            </p>
          </div>

          {/* Label */}
          <div className="h-px bg-softRose dark:bg-gray-600 opacity-40"></div>
          <div className="space-y-1">
            <p className="text-sm md:text-base font-semibold text-deepCharcoal dark:text-darkText">
              {label}
            </p>
            <p className="text-xs text-mutedGray dark:text-gray-400 font-light">
              {description}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export const TimeMeaning: React.FC<TimeMeaningProps> = ({
  onNext,
  onPrevious,
}) => {
  const metrics = COPY.timeMeaning.metrics.map((metric, idx) => ({
    label: metric.label,
    value: metric.value,
    unit: metric.label.toLowerCase(),
    description: metric.caption,
    delay: idx * 300,
  }));

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4 bg-warmWhite dark:bg-darkBg transition-colors duration-400 py-20">
      <div className="max-w-5xl w-full space-y-12">
        {/* Header */}
        <div className="text-center space-y-4 animate-fade-in">
          <h2 className="text-4xl md:text-5xl font-light text-deepCharcoal dark:text-darkText">
            {COPY.timeMeaning.title}
          </h2>
          <p className="text-lg text-mutedGray dark:text-gray-400 font-light">
            {COPY.timeMeaning.subtitle}
          </p>
        </div>

        {/* Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {metrics.map((metric, idx) => (
            <CounterCard
              key={idx}
              label={metric.label}
              value={metric.value}
              unit={metric.unit}
              description={metric.description}
              delay={metric.delay}
            />
          ))}
        </div>

        {/* Closing message */}
        <div
          className="text-center space-y-4 animate-fade-in"
          style={{ animationDelay: "1.5s" }}
        >
          <div className="h-px w-12 bg-heartRed dark:bg-softRose mx-auto"></div>
          <p className="text-lg text-deepCharcoal dark:text-darkText font-light italic">
            {COPY.timeMeaning.reflection}
          </p>
        </div>

        {/* Button */}
        <div className="flex justify-center pt-4">
          <Navigation
            onNext={onNext}
            onPrevious={onPrevious}
            nextLabel={COPY.timeMeaning.button}
            showPrevious={true}
          />
        </div>
      </div>
    </div>
  );
};
